// Times and distances as the crawl panel shows them (UK wall clock).

import { START_STEP_MIN } from './config';
import { durationLabel, nextDate, ukDate, ukParts } from '../lib/time';

const STEP_MS = START_STEP_MIN * 60_000;

/** Now, rounded up to the next quarter hour (UK quarters line up with UTC ones). */
export const nextQuarter = (now: number) => Math.ceil(now / STEP_MS) * STEP_MS;

/** "6:15pm", "7pm", "12:30am". */
export function timeLabel(ms: number): string {
  const { hour, minute } = ukParts(ms);
  const n = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return minute === 0 ? `${n}${suffix}` : `${n}:${String(minute).padStart(2, '0')}${suffix}`;
}

/** "7 min", "1h 5m": minutes stay spelled out (a bare "2m" reads like metres). */
export function minutesLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return m < 60 ? `${m} min` : durationLabel(m);
}

/** "550 m", "2.9 km". */
export function distanceLabel(km: number): string {
  return km < 1 ? `${Math.max(10, Math.round((km * 1000) / 10) * 10)} m` : `${km.toFixed(1)} km`;
}

export interface StartOption {
  value: number;
  label: string;
}

/**
 * Start times on offer: every quarter hour from now (rounded up) to the end
 * of tomorrow, the span the forecast covers, grouped by day.
 */
export function startTimeOptions(now: number): { day: string; options: StartOption[] }[] {
  const today = ukDate(now);
  const tomorrow = nextDate(today);
  const groups = [
    { day: 'Today', date: today, options: [] as StartOption[] },
    { day: 'Tomorrow', date: tomorrow, options: [] as StartOption[] },
  ];
  for (let t = nextQuarter(now); ; t += STEP_MS) {
    const group = groups.find((g) => g.date === ukDate(t));
    if (!group) break;
    group.options.push({ value: t, label: timeLabel(t) });
  }
  return groups.filter((g) => g.options.length > 0).map(({ day, options }) => ({ day, options }));
}

/** "1pm" today, "1pm tomorrow", else "Sat 18 Jul, 1pm": for "Start at …". */
export function startAtLabel(ms: number, now: number): string {
  const date = ukDate(ms);
  const today = ukDate(now);
  if (date === today) return timeLabel(ms);
  if (date === nextDate(today)) return `${timeLabel(ms)} tomorrow`;
  return dayTimeLabel(ms, now);
}

/** "Today 6:15pm" / "Tomorrow 7pm" / "Sat 18 Jul, 7pm". */
export function dayTimeLabel(ms: number, now: number): string {
  const date = ukDate(ms);
  const today = ukDate(now);
  if (date === today) return `today ${timeLabel(ms)}`;
  if (date === nextDate(today)) return `tomorrow ${timeLabel(ms)}`;
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(ms);
  return `${day}, ${timeLabel(ms)}`;
}
